import { useContext, useEffect, useState, useLayoutEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { CurrentUserContext } from "components/context/RequireAuth";
import FlexPage from "components/layout/FlexPage";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import useIsMobile from "components/hooks/useIsMobile";
import RsvpNoteDialog, {
  RsvpNoteDialogType,
} from "components/calendar/RsvpNoteDialog";
import { useAppBarSx } from "components/context/AppBarContext";
import { FamilyStatus } from "protogen/advisors_service_pb";
import { useGlobalModalContext } from "components/context/GlobalModalContext";
import { SYSTEM_ADVISOR_REF } from "components/common/types";
import useTitle from "components/hooks/useTitle";
import useIsDesktop from "components/hooks/useIsDesktop";
import { useGetMemberCalendarIntegrations } from "services/calendar";
import { useUpdateProductPrompts } from "services/nux";
import { HomeLayoutProps } from "./member-home/types";
import StandardHomeDesktop from "./member-home/StandardHomeDesktop";
import StandardHomeMobile from "./member-home/StandardHomeMobile";
import TrialHomeDesktop from "./member-home/TrialHomeDesktop";
import TrialHomeMobile from "./member-home/TrialHomeMobile";
import DigitalOnlyHomeDesktop from "./member-home/DigitalOnlyHomeDesktop";
import DigitalOnlyHomeMobile from "./member-home/DigitalOnlyHomeMobile";

export default () => {
  useTitle("Home");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openReview] = useState(() => searchParams.get("review") === "true");

  useEffect(() => {
    if (openReview) {
      searchParams.delete("review");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);
  const [rsvpDialogType, setRsvpDialogType] =
    useState<RsvpNoteDialogType>(null);
  const isMobile = useIsMobile();
  const { data, loading, isDigitalSubscription, isUnpaidSignup } =
    useMemberDetails();
  const currentUser = useContext(CurrentUserContext);
  const location = useLocation();
  const { setSx } = useAppBarSx();
  const [themeBase] = useState(
    document.querySelector("meta[name=theme-color]")?.getAttribute("content") ||
      undefined,
  );
  const { isModalOpen } = useGlobalModalContext();
  const [integrationCount, setIntegrationCount] = useState<number>(0);
  const [calendarTwoWaySyncUpsell, setCalendarTwoWaySyncUpsell] =
    useState<boolean>(false);
  const [showAnimations, setShowAnimations] = useState(false);
  const [productPrompts, setProductPrompts] = useState<any>(null);
  const { request: updateProductPrompts } = useUpdateProductPrompts();

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      setShowAnimations(true);
    });
  }, []);

  const homeSx = {
    background: "transparent",
    borderBottom: "none",
  };
  const originalSx = {
    background: "#FDFAF7",
    borderBottom: "1px solid #E8E8E8",
  };

  const { request: getIntegrationsRequest, loading: integrationsLoading } =
    useGetMemberCalendarIntegrations((response) => {
      if (response) {
        const count = response.importIntegrations.filter(
          (i) => i.status === "active",
        ).length;
        setIntegrationCount(count);
        const hasGoogleRead = response.importIntegrations.some(
          (i) =>
            i.type === "google" &&
            i.accessScope !== "read_write" &&
            i.status === "active",
        );
        const hasGoogleWrite = response.importIntegrations.some(
          (i) =>
            i.type === "google" &&
            i.accessScope === "read_write" &&
            i.status === "active",
        );
        setCalendarTwoWaySyncUpsell(hasGoogleRead && !hasGoogleWrite);
      }
    });

  useLayoutEffect(() => {
    if (!isMobile) {
      return;
    }
    setSx(homeSx);
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 210) {
        setSx(originalSx);
        revertThemeColor();
      } else {
        setSx(homeSx);
        const metaThemeColor = document.querySelector("meta[name=theme-color]");
        if (metaThemeColor) {
          metaThemeColor.setAttribute("content", "#D9F0EB");
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      setSx(originalSx);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]);

  const revertThemeColor = () => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", themeBase || "");
    }
  };
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        "content",
        isModalOpen ? "#6D7876" : "#D9F0EB",
      );
    } else {
      revertThemeColor();
    }
    return () => {
      revertThemeColor();
    };
  }, [isModalOpen]);

  useEffect(() => {
    getIntegrationsRequest({
      includeMemberEmailProviders: false,
    });
    if (location.state?.calendarEvent?.action === "confirm") {
      setRsvpDialogType("confirm");
    }
    if (location.state?.calendarEvent?.action === "invalid") {
      setRsvpDialogType("invalid");
    }
  }, []);

  useEffect(() => {
    if (data?.member) {
      const parsedPrompts = JSON.parse(data.member.productPrompts || "{}");
      setProductPrompts(parsedPrompts);
    }
  }, [data?.member]);

  if (data?.family?.status === FamilyStatus.PROSPECT) {
    navigate("/welcome/landing");
  }
  const isDeactivated =
    data?.family?.status === FamilyStatus.DEACTIVATED ||
    data?.family?.status === FamilyStatus.DIGITAL_DEACTIVATED ||
    data?.family?.status === FamilyStatus.DIGITAL_DEACTIVATED_TRIAL ||
    data?.family?.status === FamilyStatus.DEACTIVATED_PROSPECT;
  const isSystemAdvisor = data?.primaryAdvisor?.ref === SYSTEM_ADVISOR_REF;
  const isDesktop = useIsDesktop();

  const handleDismissCalendarBanner = async () => {
    await updateProductPrompts({
      key: "offloadThinkingWorkshopDismissed",
      value: "dismissed",
    });
    setProductPrompts((prev: any) => ({
      ...prev,
      offloadThinkingWorkshopDismissed: "dismissed",
    }));
  };

  const handleDismissFeatureSuggestions = async () => {
    await updateProductPrompts({
      key: "featureSuggestionsDismissed",
      value: "dismissed",
    });
    setProductPrompts((prev: any) => ({
      ...prev,
      featureSuggestionsDismissed: "dismissed",
    }));
  };

  const handleDismissLightningBanner = async () => {
    await updateProductPrompts({
      key: "lightningTaskBannerDismissed",
      value: "dismissed",
    });
    setProductPrompts((prev: any) => ({
      ...prev,
      lightningTaskBannerDismissed: "dismissed",
    }));
  };

  const handleLightningBannerClick = () => {
    navigate("/tasks/add?lightning=true");
  };

  const handleDismissInspirationBanner = async () => {
    await updateProductPrompts({
      key: "inspirationBannerDismissed",
      value: "dismissed",
    });
    setProductPrompts((prev: any) => ({
      ...prev,
      inspirationBannerDismissed: "dismissed",
    }));
  };

  const handleDismissReviewPrompt = async () => {
    await updateProductPrompts({
      key: "reviewPromptDismissed",
      value: "dismissed",
    });
    setProductPrompts((prev: any) => ({
      ...prev,
      reviewPromptDismissed: "dismissed",
    }));
  };

  const homeLayoutProps: HomeLayoutProps = {
    data,
    loading,
    currentUser,
    integrationCount,
    integrationsLoading,
    calendarTwoWaySyncUpsell,
    showAnimations,
    isDesktop,
    isMobile,
    isSystemAdvisor,
    isDeactivated,
    productPrompts,
    handleDismissCalendarBanner,
    handleDismissFeatureSuggestions,
    handleDismissLightningBanner,
    handleLightningBannerClick,
    handleDismissReviewPrompt,
    handleDismissInspirationBanner,
    openReview,
  };

  return (
    <FlexPage
      sx={{
        maxWidth: "100%",
        borderRadius: "16px 0 0 0",
        overflow: isMobile ? "visible" : "auto",
        padding: isDesktop ? "48px 48px 32px" : "24px",
        background: isMobile
          ? "#FFF"
          : "radial-gradient(36.57% 38.63% at 75.43% 53.79%, rgba(213, 104, 135, 0.20) 0%, rgba(213, 104, 135, 0.00) 100%), radial-gradient(73.43% 67.94% at 8.96% 100%, rgba(243, 213, 205, 0.55) 0%, rgba(255, 255, 255, 0.00) 100%), radial-gradient(88.17% 83.57% at 29.56% 16.11%, rgba(161, 222, 219, 0.35) 0%, rgba(255, 255, 255, 0.00) 100%), #FEF8F5",
      }}
      leftAligned
      headerPadding={false}
      fullHeight={true}
    >
      {isDigitalSubscription ? (
        isMobile ? (
          <DigitalOnlyHomeMobile {...homeLayoutProps} />
        ) : (
          <DigitalOnlyHomeDesktop {...homeLayoutProps} />
        )
      ) : isUnpaidSignup ? (
        isMobile ? (
          <TrialHomeMobile {...homeLayoutProps} />
        ) : (
          <TrialHomeDesktop {...homeLayoutProps} />
        )
      ) : isMobile ? (
        <StandardHomeMobile {...homeLayoutProps} />
      ) : (
        <StandardHomeDesktop {...homeLayoutProps} />
      )}
      <RsvpNoteDialog
        open={!!rsvpDialogType}
        onClose={() => setRsvpDialogType(null)}
        type={rsvpDialogType}
      />
    </FlexPage>
  );
};
